import React, { useRef } from "react";
import { withRouter } from "react-router";
import compose from "recompose/compose";
import useEnsureLoggedInPromise from "./useEnsureLoggedInPromise";
import useRefreshing from "theme/modules/Router/useRefreshing";

/**
 * @callback EnsureLoggedInFunction
 * @returns {Promise<React.MutableRefObject<boolean>>}
 */

/**
 * @callback ReloadIfNewLoginFunction
 */

/**
 * @typedef {object} OutputProps
 * @property {JSX.Element} loginModal
 * @property {EnsureLoggedInFunction} ensureLoggedIn
 * @property {ReloadIfNewLoginFunction} reloadIfNewLogin
 */

/**
 * @callback EnsureLoggedInHOC
 * @param {React.ComponentType<BaseProps & OutputProps>} BaseComponent
 * @returns {React.FunctionComponent<BaseProps>}
 * @template BaseProps
 */

/**
 * @param {{ message: string | JSX.Element }} param0
 * @returns {EnsureLoggedInHOC<BaseProps>}
 * @template BaseProps
 */
const withEnsureLoggedIn = ({ message }) =>
  compose(withRouter, (BaseComponent) => (props) => {
    const needsReload = useRef(false);
    const { modal, ensureLoggedIn } = useEnsureLoggedInPromise({
      message: message,
    });

    const [, setRefreshing] = useRefreshing();

    return (
      <BaseComponent
        {...props}
        loginModal={modal}
        ensureLoggedIn={() =>
          ensureLoggedIn()
            .then((wasAlreadyLoggedIn) => {
              needsReload.current = !wasAlreadyLoggedIn;
              return needsReload;
            })
            .catch((e) => {
              needsReload.current = false;
              throw e;
            })
        }
        reloadIfNewLogin={() => {
          if (needsReload.current) {
            window.location.reload();
            setRefreshing(true);
          }
        }}
      />
    );
  });

export default withEnsureLoggedIn;
